import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { affiliateService } from '@/services/affiliateService';
import type { CreateAffiliateRequest, UpdateAffiliateRequest } from '@/services/affiliateService';

export function useAffiliate(id: string) {
  return useQuery({
    queryKey: ['affiliate', id],
    queryFn: () => affiliateService.getAffiliate(id),
    enabled: !!id,
  });
}

export function useAffiliateByCode(code: string) {
  return useQuery({
    queryKey: ['affiliate', 'code', code],
    queryFn: () => affiliateService.getAffiliateByCode(code),
    enabled: !!code,
  });
}

export function useAffiliateByUserID(userId: string) {
  return useQuery({
    queryKey: ['affiliate', 'user', userId],
    queryFn: () => affiliateService.getAffiliateByUserID(userId),
    enabled: !!userId,
  });
}

export function useMyAffiliate(affiliateId: string) {
  return useQuery({
    queryKey: ['affiliate', affiliateId],
    queryFn: () => affiliateService.getMyAffiliate(affiliateId),
    enabled: !!affiliateId,
  });
}

export function useAffiliateStats(id: string) {
  return useQuery({
    queryKey: ['affiliate', id, 'stats'],
    queryFn: () => affiliateService.getClickStats(id),
    enabled: !!id,
  });
}

export function useAffiliateClicks(id: string) {
  return useQuery({
    queryKey: ['affiliate', id, 'clicks'],
    queryFn: () => affiliateService.getAffiliateClicks(id),
    enabled: !!id,
  });
}

export function useCreateAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAffiliateRequest) => affiliateService.createAffiliate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
  });
}

export function useUpdateAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAffiliateRequest }) =>
      affiliateService.updateAffiliate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['affiliate', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
  });
}

export function useDeactivateAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => affiliateService.deactivateAffiliate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['affiliate', id] });
      queryClient.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
  });
}

export function useTrackClick() {
  return useMutation({
    mutationFn: ({ code, landingPage }: { code: string; landingPage: string }) =>
      affiliateService.trackClick(code, landingPage),
  });
}

export function useGenerateReferralURL() {
  return useMutation({
    mutationFn: ({ affiliateId, baseUrl }: { affiliateId: string; baseUrl: string }) =>
      affiliateService.generateReferralURL(affiliateId, baseUrl),
  });
}
